/**
 * Detect Supabase password-recovery sessions from the access token JWT.
 * Used client-side for routing; server-side enforcement is in tenant_claims_hook (no tenant JWT claims on recovery).
 */

/** Legacy sessionStorage key. NEVER read any more (KAN-1033): a stale value in a tab
 *  caused a `/` <-> `/reset-password` redirect loop. Wiped once at load. */
export const PASSWORD_RECOVERY_FLOW_SESSION_KEY = 'cv_password_recovery_flow';
try { sessionStorage.removeItem(PASSWORD_RECOVERY_FLOW_SESSION_KEY); } catch { /* ignore */ }

/**
 * In-MEMORY state only (dies with the page, can never go stale across loads):
 *  - `verifyWindow`: ResetPassword's own verifyOtp is in flight / its link is being applied.
 *  - `recoveryUserId`: the user a GoTrue recovery verify / PASSWORD_RECOVERY event
 *    authenticated in THIS page life — makes that user's session a recovery session even
 *    when its JWT omits the `recovery` amr, and nobody else's.
 */
let verifyWindow = false;
let recoveryUserId = null;

/** Opens the in-page verify window (called right before verifyOtp). */
export function markPasswordRecoveryFlowFromUrl() {
  verifyWindow = true;
}

/** Closes the verify window only (the recovery user marker stays). */
export function endPasswordRecoveryVerifyWindow() {
  verifyWindow = false;
}

/** Closes everything: verify window AND the recovery-user marker. */
export function clearPasswordRecoveryFlowIntent() {
  verifyWindow = false;
  recoveryUserId = null;
}

export function isPasswordRecoveryFlowIntent() {
  return verifyWindow;
}

/** A recovery verify / PASSWORD_RECOVERY event authenticated this user in this page life. */
export function noteRecoveryEventUser(userId) {
  if (userId) recoveryUserId = userId;
}

function decodeJwtPayload(accessToken) {
  if (!accessToken || typeof accessToken !== 'string') return null;
  try {
    const part = accessToken.split('.')[1];
    if (!part) return null;
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

/**
 * Recovery sessions are indicated on the JWT `amr` claim.
 * Supabase may use object entries `{ method: 'recovery', ... }` or RFC-8176 string entries.
 */
export function isPasswordRecoverySession(session) {
  if (recoveryUserId && session?.user?.id === recoveryUserId) return true;
  const payload = decodeJwtPayload(session?.access_token);
  if (!payload?.amr || !Array.isArray(payload.amr)) return false;
  return payload.amr.some((entry) => {
    if (entry && typeof entry === 'object' && entry.method === 'recovery') return true;
    if (typeof entry === 'string' && entry === 'recovery') return true;
    return false;
  });
}
